/**
 * Etapa "Liberar compra": busca fixa no topo; grid com scroll interno.
 * @module ReleaseProductsSection
 */

import { memo, useMemo, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  woocommerceStoreService,
  type PurchaseReleaseLineInput,
  type WooProduct,
} from "src-next/modules/patient-supporting-documents";
import { WooProductGrid } from "src-next/modules/patient-supporting-documents/marketplace/components/WooProductGrid";

interface ReleaseProductsSectionProps {
  storeName?: string;
  products: WooProduct[];
  productsLoading: boolean;
  productsError: string | null;
  lines: PurchaseReleaseLineInput[];
  onAddProduct: (product: WooProduct) => void;
  onUpdateQuantity: (productId: number, quantity: number) => void;
}

export const ReleaseProductsSection = memo(function ReleaseProductsSection({
  products,
  productsLoading,
  productsError,
  lines,
  onAddProduct,
  onUpdateQuantity,
}: ReleaseProductsSectionProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const visibleProducts = useMemo(() => {
    const purchasable = products.filter((product) => product.purchasable);
    return woocommerceStoreService.filterProductsLocally(
      purchasable,
      searchTerm,
    );
  }, [products, searchTerm]);

  const getItemQuantity = (productId: number) =>
    lines.find((line) => line.product.id === productId)?.quantity ?? 0;

  return (
    <Stack
      spacing={1.5}
      sx={{ height: "100%", minHeight: 0, overflow: "hidden" }}
    >
      <Paper variant="outlined" sx={{ p: 1.5, flexShrink: 0 }}>
        <TextField
          size="small"
          label="Buscar produto"
          placeholder="Nome ou SKU"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Limpar busca de produto"
                  onClick={() => setSearchTerm("")}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Paper>

      {!productsLoading && lines.length === 0 && (
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon />}
          sx={{ flexShrink: 0, py: 0.5 }}
        >
          Toque em <strong>Adicionar</strong> nos produtos que o paciente pode
          comprar.
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          pr: 0.5,
          WebkitOverflowScrolling: "touch",
        }}
      >
        <WooProductGrid
          products={visibleProducts}
          loading={productsLoading}
          error={productsError}
          onAddToCart={onAddProduct}
          getItemQuantity={getItemQuantity}
          onUpdateQuantity={onUpdateQuantity}
          hidePrice
          compact
        />
      </Box>
    </Stack>
  );
});
